import React from 'react';

import { useQuery } from '@tanstack/react-query';

import { getTrendingSearches } from '~/searchCio/apis/getTrendingSearches';
import {
  AutoSuggestList,
  AutoSuggestListItem,
  AutoSuggestSection,
  AutoSuggestSectionTitle,
} from '~/searchCio/components/sharedComponents/productComponents';
import {
  QUERY_TYPE_TRENDING_SEARCHES,
  RECOMMENDATION_API_STALE_TIME,
} from '~/searchCio/constants/cioConstants';
import useCioAutosuggest from '~/searchCio/hooks/useCioAutosuggest';
import { useCioClient } from '~/shared/utils/useCioClient';
import { useBreakpoint } from '~/techstyle-shared/react-components';
import { FormattedMessage } from '~/techstyle-shared/react-intl';

import TextListSkeletonLoaderDesktop from './TextListSkeletonLoaderDesktop';
import TextListSkeletonLoaderMobile from './TextListSkeletonLoaderMobile';

const TrendingSearchesSection = () => {
  const cioClient = useCioClient();

  const { data, isFetching, isLoading, error } = useQuery({
    queryKey: ['Trending Searches'],
    queryFn: () => getTrendingSearches({ cioClient }),
    retry: 1,
    staleTime: RECOMMENDATION_API_STALE_TIME,
  });
  const { isMobile } = useBreakpoint();
  const { handleSearchTermSelect } = useCioAutosuggest();

  // Check if loading, fetching or error, or data is 0, then show loader
  if ((isLoading && isFetching) || error || !data?.trendingSearches?.length) {
    return isMobile ? (
      <TextListSkeletonLoaderMobile />
    ) : (
      <TextListSkeletonLoaderDesktop />
    );
  }

  return (
    <AutoSuggestSection
      data-cnstrc-recommendations
      data-cnstrc-recommendations-pod-id={data.podId}
      data-cnstrc-result-id={data.resultId}
      data-cnstrc-num-results={data.trendingSearches.length}
      data-testid="cnstrc-trending-searches"
    >
      <AutoSuggestSectionTitle role="heading" id="trending_searches_title">
        <FormattedMessage
          id="search_autosuggest.trending_searches_header"
          defaultMessage="Trending Searches"
        />
      </AutoSuggestSectionTitle>
      <AutoSuggestList aria-labelledby="trending_searches_title">
        {data.trendingSearches.map((searchTerm, index) => (
          <AutoSuggestListItem
            role="link"
            data-autotag="trending_search_list_item"
            key={searchTerm.value}
            tabIndex={0}
            onClick={() => {
              handleSearchTermSelect({
                searchTerm: searchTerm.value,
                queryType: QUERY_TYPE_TRENDING_SEARCHES,
              });
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearchTermSelect({
                  searchTerm: searchTerm.value,
                  queryType: QUERY_TYPE_TRENDING_SEARCHES,
                });
              }
            }}
            data-cnstrc-item="recommendation"
            data-cnstrc-item-id={searchTerm.result_id}
            data-cnstrc-item-name={searchTerm.value}
            data-cnstrc-strategy-id={searchTerm.strategy.id}
            data-testid={`cnstrc-trending-search-${index}`}
          >
            {searchTerm.value}
          </AutoSuggestListItem>
        ))}
      </AutoSuggestList>
    </AutoSuggestSection>
  );
};

export default TrendingSearchesSection;
